import React from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BottomNav } from '@/components/BottomNav';
import { useAuth } from '@/contexts/AuthContext';
import { Heart, LayoutDashboard, Users, Settings, LogOut, FilePlus, MapPinned, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Toaster } from 'sonner';

const ROLE_LABELS = { admin: 'Admin', hauptmitarbeiter: 'Hauptmitarbeiter', mitarbeiter: 'Mitarbeiter' };

export default function Layout() {
  const { user, logout, isAdmin, canAccessTourenplanung } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/patienten', icon: Users, label: 'Patienten', show: true },
    { path: '/tourenplanung', icon: MapPinned, label: 'Tourenplanung', show: canAccessTourenplanung },
    { path: '/neuer-bericht', icon: FilePlus, label: 'Neuer Bericht', show: true },
    { path: '/admin', icon: ShieldCheck, label: 'Verwaltung', show: isAdmin },
    { path: '/einstellungen', icon: Settings, label: 'Einstellungen', show: true },
  ].filter(i => i.show);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-slate-200 z-40">
        <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-200">
          <div className="w-9 h-9 bg-teal-700 rounded-xl flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-heading font-bold text-slate-900 text-lg">WundDok</span>
            <p className="text-[10px] text-slate-400 -mt-0.5">Wunddokumentation</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {sidebarItems.map(({ path, icon: Icon, label }) => {
            const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
            return (
              <NavLink
                key={path}
                to={path}
                data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-teal-50 text-teal-700 border border-teal-100'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold text-sm">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <Badge variant="outline" className="text-[10px] mt-0.5 border-slate-200 text-slate-500" data-testid="user-role-badge">
                {ROLE_LABELS[user?.role] || user?.role}
              </Badge>
            </div>
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout-btn"
              className="p-2 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-all"
              title="Abmelden"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200 h-14">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-700 rounded-lg flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-slate-900">WundDok</span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
            className="p-2 text-slate-400 hover:text-rose-600 rounded-lg"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <BottomNav />
      <Toaster position="top-center" richColors />
    </div>
  );
}
